import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, Quote } from "lucide-react";

export default function HighlightDialog({ open, onOpenChange, bookId, bookTitle, onSaved }) {
  const [quote, setQuote] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!quote.trim()) {
      toast.error("Paste or type the passage first");
      return;
    }
    setSaving(true);
    try {
      await api.post("/highlights", { book_id: bookId, quote, note });
      toast.success("Highlight saved");
      setQuote("");
      setNote("");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save highlight");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="highlight-dialog" className="max-w-lg bg-paper border border-edge rounded-none">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Bookmark className="w-4 h-4 text-terracotta" />
            <span className="label-eyebrow text-sage">Save a highlight</span>
          </div>
          <DialogTitle className="font-serif text-2xl leading-tight">
            From <em className="not-italic text-terracotta">{bookTitle}</em>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-3">
          <div>
            <label className="label-eyebrow block mb-1 flex items-center gap-1"><Quote className="w-3 h-3" /> The passage</label>
            <textarea
              data-testid="highlight-quote-input"
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              rows={5}
              placeholder="Paste or type the line you want to keep…"
              className="w-full border border-edge bg-white px-3 py-2 text-sm font-serif focus:outline-none focus:border-ink resize-none"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">A note (optional)</label>
            <textarea
              data-testid="highlight-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Why this matters to you"
              className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink resize-none"
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-edge pt-4">
            <button
              onClick={() => onOpenChange(false)}
              className="text-xs uppercase tracking-widest px-4 py-2 text-muted_ink hover:text-ink"
            >
              Cancel
            </button>
            <button
              data-testid="highlight-save-btn"
              onClick={save}
              disabled={saving}
              className="btn-primary disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save highlight"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
